import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { environment } from 'environments/environment';
import { Observable, Subject, catchError, filter, finalize, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private _http = inject(HttpClient);
  private _router = inject(Router);
  private _headers!: HttpHeaders;
  private _baseUrl = `${environment.apiUrl}/api`;
  private _requestCancelMap = new Map<string, Subject<void>>();

  constructor() {
    this._headers = new HttpHeaders().set('Content-Type', 'application/json');
    this._headers = this._headers.append('Accept', 'application/json');
    this._headers = this._headers.append('Access-Control-Allow-Origin', '*');

    // Cancel all in-flight requests when navigating between routes/modules.
    this._router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event) => {
        const navigationEvent = event as NavigationStart;
        const currentRoute = this.stripFragment(this._router.url);
        const nextRoute = this.stripFragment(navigationEvent.url);

        // Ignore fragment-only navigation so tab/hash changes do not cancel in-flight calls.
        if (currentRoute !== nextRoute) {
          this.cancelAllPendingRequests();
        }
      });
  }

  //#region API Calls

  /** Builds the http `GET` for md comp
   * @param T - the type of object to return
   * @param url - the url to call (e.g. 'controller/function') base url and 'api' are added in this service
   * @param httpParams - optional params to pass to the get request for filtering "?key=value"
   * @param cancelInFlightByUrl - when true, cancels any in-flight GET to the same URL before issuing a new one
   * @return `Observable<T>` - the object returned from the get request as the body
   */
  httpGet<T>(
    url: string,
    httpParams: Map<string, string> | undefined = undefined,
    cancelInFlightByUrl: boolean = true,
  ): Observable<T> {
    let params = new HttpParams();
    if (httpParams) {
      if (httpParams.size > 0) {
        httpParams.forEach((value: string, key: string) => {
          params = params.append(key, value);
        });
      }
    }
    const options = { headers: this._headers, params: params };

    const request$ = this._http.get<T>(`${this._baseUrl}/${url}`, options);
    return this.executeWithCancellation(
      this.buildRequestKey('GET', url, params),
      request$,
      cancelInFlightByUrl,
    );
  }

  /** Builds the http `POST` for md comp
   * @param T - the type of object to return
   * @param url - the url to call (e.g. 'controller/function') base url and 'api' are added in this service
   * @param body - the object to pass to the post request as the body
   * @returns `Observable<T>` - the object returned from the post request as the body
   */
  httpPost<T>(
    url: string,
    body: T,
    httpParams: Map<string, any> | undefined = undefined,
    cancelInFlightByUrl: boolean = true,
  ): Observable<T> {
    let params = new HttpParams();
    if (httpParams) {
      if (httpParams.size > 0) {
        httpParams.forEach((value: any, key: string) => {
          params = params.append(key, value);
        });
      }
    }

    const options = { headers: this._headers, params: params };

    const request$ = this._http.post<T>(`${this._baseUrl}/${url}`, body, options);
    return this.executeWithCancellation(
      this.buildRequestKey('POST', url, params),
      request$,
      cancelInFlightByUrl,
    );
    //.pipe(catchError(this.handleError));
  }

  httpPostGeneric<T, S>(
    url: string,
    body: T,
    httpParams: Map<string, any>,
    cancelInFlightByUrl: boolean = true,
  ): Observable<S> {
    let params = new HttpParams();
    if (httpParams) {
      if (httpParams.size > 0) {
        httpParams.forEach((value: any, key: string) => {
          params = params.append(key, value);
        });
      }
    }
    const options = { headers: this._headers, params: params };

    const request$ = this._http.post<S>(`${this._baseUrl}/${url}`, body, options);
    return this.executeWithCancellation(
      this.buildRequestKey('POST', url, params),
      request$,
      cancelInFlightByUrl,
    );
  }

  httpPutGeneric<T, S>(
    url: string,
    body: T,
    httpParams: Map<string, any> | undefined = undefined,
    cancelInFlightByUrl: boolean = true,
  ): Observable<S> {
    let params = new HttpParams();
    if (httpParams) {
      if (httpParams.size > 0) {
        httpParams.forEach((value: any, key: string) => {
          params = params.append(key, value);
        });
      }
    }
    const options = { headers: this._headers, params: params };
    const request$ = this._http.put<S>(`${this._baseUrl}/${url}`, body, options);
    return this.executeWithCancellation(
      this.buildRequestKey('PUT', url, params),
      request$,
      cancelInFlightByUrl,
    );
    //.pipe(catchError(this.handleError));
  }

  httpPut<T>(
    url: string,
    body: T,
    httpParams: Map<string, any> | undefined = undefined,
    cancelInFlightByUrl: boolean = true,
  ): Observable<T> {
    let params = new HttpParams();
    if (httpParams) {
      if (httpParams.size > 0) {
        httpParams.forEach((value: any, key: string) => {
          params = params.append(key, value);
        });
      }
    }
    const options = { headers: this._headers, params: params };
    const request$ = this._http.put<T>(`${this._baseUrl}/${url}`, body, options);
    return this.executeWithCancellation(
      this.buildRequestKey('PUT', url, params),
      request$,
      cancelInFlightByUrl,
    );
    //.pipe(catchError(this.handleError));
  }

  httpDelete<T>(url: string, cancelInFlightByUrl: boolean = true): Observable<T> {
    const request$ = this._http.delete<T>(`${this._baseUrl}/${url}`, { headers: this._headers });
    return this.executeWithCancellation(
      this.buildRequestKey('DELETE', url),
      request$,
      cancelInFlightByUrl,
    );
    //.pipe(catchError(this.handleError));
  }

  private executeWithCancellation<T>(
    requestKey: string,
    request$: Observable<T>,
    cancelInFlightByUrl: boolean,
  ): Observable<T> {
    const requestWithErrorLogging$ = request$.pipe(
      catchError((error: unknown) => this.logAndRethrowRequestError(requestKey, error)),
    );

    if (!cancelInFlightByUrl) {
      return requestWithErrorLogging$;
    }

    const cancel$ = this.cancelPreviousRequest(requestKey);
    return this.createSilentCancellationStream(requestWithErrorLogging$, cancel$).pipe(
      finalize(() => this.cleanupCancelKey(requestKey, cancel$)),
    );
  }

  private createSilentCancellationStream<T>(
    request$: Observable<T>,
    cancel$: Subject<void>,
  ): Observable<T> {
    return new Observable<T>((observer) => {
      const requestSub = request$.subscribe({
        next: (value) => observer.next(value),
        error: (error) => observer.error(error),
        complete: () => observer.complete(),
      });

      const cancelSub = cancel$.subscribe(() => {
        // Cancel the in-flight request and complete the stream so callers can clear loading state.
        requestSub.unsubscribe();
        cancelSub.unsubscribe();
        observer.complete();
      });

      return () => {
        cancelSub.unsubscribe();
        requestSub.unsubscribe();
      };
    });
  }

  private logAndRethrowRequestError(requestKey: string, error: unknown): Observable<never> {
    if (error instanceof HttpErrorResponse) {
      const headers: Record<string, string | null> = {};
      error.headers.keys().forEach((key) => {
        headers[key] = error.headers.get(key);
      });

      console.error('API request failed', {
        requestKey,
        status: error.status,
        statusText: error.statusText,
        message: error.message,
        name: error.name,
        ok: error.ok,
        responseUrl: error.url,
        errorBody: error.error,
        headers,
      });
    } else {
      console.error('API request failed', { requestKey, error });
    }

    return throwError(() => error);
  }

  private cancelPreviousRequest(key: string): Subject<void> {
    const existingCancel$ = this._requestCancelMap.get(key);
    if (existingCancel$) {
      existingCancel$.next();
      existingCancel$.complete();
    }

    const cancel$ = new Subject<void>();
    this._requestCancelMap.set(key, cancel$);
    return cancel$;
  }

  private cleanupCancelKey(key: string, cancel$: Subject<void>): void {
    if (this._requestCancelMap.get(key) === cancel$) {
      this._requestCancelMap.delete(key);
    }
  }

  private cancelAllPendingRequests(): void {
    this._requestCancelMap.forEach((cancel$) => {
      cancel$.next();
      cancel$.complete();
    });
    this._requestCancelMap.clear();
  }

  private buildRequestKey(method: string, url: string, params?: HttpParams): string {
    const queryString = params?.toString();
    return queryString ? `${method}:${url}?${queryString}` : `${method}:${url}`;
  }

  private stripFragment(url: string): string {
    return url.split('#')[0];
  }
}
